import { type ChangeEvent, useActionState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Input } from 'src/components/ui/input';

type ForecastPaginatedActionsRenameSessionModalProps = {
  open: boolean;
  sessionName: string;
  isSubmitting: boolean;
  onSessionNameChange: (value: string) => void;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
};

export function ForecastPaginatedActionsRenameSessionModal({
  open,
  sessionName,
  isSubmitting,
  onSessionNameChange,
  onConfirm,
  onCancel,
}: ForecastPaginatedActionsRenameSessionModalProps) {
  const { t } = useTranslation('forecast');
  const [, submitRenameAction, isActionPending] = useActionState(async () => {
    await onConfirm();
    return null;
  }, null);
  const isSubmitPending = isSubmitting || isActionPending;

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSessionNameChange(event.target.value);
  };

  return (
    <Dialog open={open}>
      <DialogContent className='max-h-[80vh] max-w-md' closable={false}>
        <DialogHeader>
          <DialogTitle>{t('Name your new session')}</DialogTitle>
          <DialogDescription>
            {t(
              'A new empty session was created because no other open sessions were found',
            )}
          </DialogDescription>
        </DialogHeader>
        <form action={submitRenameAction} className='grid gap-4'>
          <div className='grid gap-2'>
            <span className='font-medium text-layout-foreground text-sm'>
              {t('Next session name')}
            </span>
            <Input
              autoFocus
              maxLength={255}
              name='sessionName'
              onChange={handleInputChange}
              placeholder={t('Next session name')}
              value={sessionName}
            />
          </div>
          <DialogFooter className='gap-2'>
            <Button
              disabled={isSubmitPending}
              onClick={onCancel}
              type='button'
              variant='outline'
            >
              {t('Cancel')}
            </Button>
            <Button disabled={isSubmitPending || !sessionName.trim()}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
